import {API_PATH} from '@app/common/constants';
import {NetworkService, ToasterService} from '@app/services';
import {takeLatest, put, call} from 'redux-saga/effects';
import {
  pobReportActions,
  fetchPriorityProductsCreatorTypeName,
  fetchAverageSalePricesCreatorTypeName,
  savePobMasterDataCreatorTypeName,
  fetchActiveSkuDataCreatorTypeName,
  updatePriorityProductsCreatorTypeName,
  updateAverageSalePriceCreatorTypeName,
} from './slice';
import { ToastType } from '@app/services/toaster';

/**
 * Watcher for fetching priority products
 */
export function* fetchPriorityProductsWatcher() {
  yield takeLatest(
    fetchPriorityProductsCreatorTypeName,
    fetchPriorityProductsWorker,
  );
}

/**
 * Worker for fetching priority products via GET API
 * @param {object} action - contains divisionId in payload
 */
export function* fetchPriorityProductsWorker(action: any): any {
  try {
    yield put(pobReportActions.setPriorityProductsLoading(true));
    const {divisionId, skuName} = action.payload;
    const baseUrl = `${API_PATH.pobManagement.priorityProducts}`;
    const queryString = skuName
      ? `divisionId=${divisionId}&skuName=${encodeURIComponent(skuName)}`
      : `divisionId=${divisionId}`;
    const res = yield call(NetworkService.get, `${baseUrl}?${queryString}`, {}, {});
    if (res?.data) {
      yield put(pobReportActions.setPriorityProducts(res.data));
    } else {
      yield put(pobReportActions.setPriorityProducts([]));
    }
    yield put(pobReportActions.setPriorityProductsLoading(false));
  } catch (error) {
    yield put(pobReportActions.setPriorityProductsLoading(false));
  }
}

/**
 * Watcher for fetching average sale prices
 */
export function* fetchAverageSalePricesWatcher() {
  yield takeLatest(
    fetchAverageSalePricesCreatorTypeName,
    fetchAverageSalePricesWorker,
  );
}

/**
 * Worker for fetching average sale prices via GET API
 * @param {object} action - contains divisionId in payload
 */
export function* fetchAverageSalePricesWorker(action: any): any {
  try {
    yield put(pobReportActions.setAverageSalePricesLoading(true));
    const {divisionId, skuName} = action.payload;
    const baseUrl = `${API_PATH.pobManagement.averageSalePrices}`;
    const queryString = skuName
      ? `divisionId=${divisionId}&skuName=${encodeURIComponent(skuName)}`
      : `divisionId=${divisionId}`;
    const res = yield call(NetworkService.get, `${baseUrl}?${queryString}`, {}, {});
    if (res?.data) {
      yield put(pobReportActions.setAverageSalePrices(res.data));
    } else {
      yield put(pobReportActions.setAverageSalePrices([]));
    }
    yield put(pobReportActions.setAverageSalePricesLoading(false));
  } catch (error) {
    yield put(pobReportActions.setAverageSalePricesLoading(false));
  }
}

/**
 * Watcher for saving POB master data
 */
export function* savePobMasterDataWatcher() {
  yield takeLatest(
    savePobMasterDataCreatorTypeName,
    savePobMasterDataWorker,
  );
}

/**
 * Worker for saving POB master data via POST API
 * @param {object} action - contains request body in payload
 */
export function* savePobMasterDataWorker(action: any): any {
  try {
    const {onSuccess, ...payload} = action.payload;
    const url = `${API_PATH.pobManagement.savePobMasterData}`;
    const res = yield call(NetworkService.post, url, payload);
    if (res?.status === 200 && !res?.data?.error) {
      ToasterService.showToaster(res?.data?.message || 'POB master data saved successfully', ToastType.SUCCESS);
      if (onSuccess) onSuccess();
    } else {
      ToasterService.showToaster(res?.data?.error || res?.data?.message || 'Failed to save POB master data', ToastType.ERROR);
    }
  } catch (error) {
    ToasterService.showToaster('Failed to save POB master data', ToastType.ERROR);
  }
}

/**
 * Watcher for fetching active SKU average sale price data
 */
export function* fetchActiveSkuDataWatcher() {
  yield takeLatest(
    fetchActiveSkuDataCreatorTypeName,
    fetchActiveSkuDataWorker,
  );
}

/**
 * Worker for fetching active SKU data via GET API
 * @param {object} action - contains divisionId in payload
 */
export function* fetchActiveSkuDataWorker(action: any): any {
  try {
    yield put(pobReportActions.setActiveSkuDataLoading(true));
    const {divisionId} = action.payload;
    const url = `${API_PATH.pobManagement.activeSkuAvgSalePriceData}`;
    const res = yield call(NetworkService.get, url, {divisionId}, {});
    if (res?.data) {
      yield put(pobReportActions.setActiveSkuData(res.data));
    } else {
      yield put(pobReportActions.setActiveSkuData([]));
    }
    yield put(pobReportActions.setActiveSkuDataLoading(false));
  } catch (error) {
    yield put(pobReportActions.setActiveSkuDataLoading(false));
  }
}

/**
 * Watcher for updating priority products
 */
export function* updatePriorityProductsWatcher() {
  yield takeLatest(
    updatePriorityProductsCreatorTypeName,
    updatePriorityProductsWorker,
  );
}

/**
 * Worker for updating priority products via PUT API
 * @param {object} action - contains update request body in payload
 */
export function* updatePriorityProductsWorker(action: any): any {
  try {
    const {onSuccess, ...payload} = action.payload;
    const url = `${API_PATH.pobManagement.updatePriorityProducts}`;
    const res = yield call(NetworkService.put, url, payload);
    if (res?.status === 200 && !res?.data?.error) {
      ToasterService.showToaster(res?.data?.message || 'Priority product updated successfully', ToastType.SUCCESS);
      if (onSuccess) onSuccess();
    } else {
      ToasterService.showToaster(res?.data?.error || res?.data?.message || 'Failed to update priority product', ToastType.ERROR);
    }
  } catch (error) {
    ToasterService.showToaster('Failed to update priority product', ToastType.ERROR);
  }
}

/**
 * Watcher for updating average sale price
 */
export function* updateAverageSalePriceWatcher() {
  yield takeLatest(
    updateAverageSalePriceCreatorTypeName,
    updateAverageSalePriceWorker,
  );
}

/**
 * Worker for updating average sale price via PUT API
 * @param {object} action - contains update request body in payload
 */
export function* updateAverageSalePriceWorker(action: any): any {
  try {
    const {onSuccess, ...payload} = action.payload;
    const url = `${API_PATH.pobManagement.updateAverageSalePrice}`;
    const res = yield call(NetworkService.put, url, payload);
    if (res?.status === 200 && !res?.data?.error) {
      ToasterService.showToaster('Average sale price updated successfully', ToastType.SUCCESS);
      if (onSuccess) onSuccess();
    } else {
      ToasterService.showToaster(res?.data?.error || res?.data?.message || 'Failed to update average sale price', ToastType.ERROR);
    }
  } catch (error) {
    ToasterService.showToaster('Failed to update average sale price', ToastType.ERROR);
  }
}
